<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import SectionTitle from '@/components/common/section-title.vue';
import { AnimatedContent } from '@/components/vuebits';

const { t } = useI18n();
const activeIndex = ref(0);

const items = computed(() =>
  Array.from({ length: 12 }, (_, i) => ({
    id: i + 1,
    q: t(`faq.items.${i}.q`),
    a: t(`faq.items.${i}.a`),
  })),
);

const current = computed(() => items.value[activeIndex.value] ?? items.value[0]);

function selectFaq(index) {
  activeIndex.value = index;
}

/** 仅桌面悬停切换 */
function onFaqHover(index) {
  if (typeof window === 'undefined') return;
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  activeIndex.value = index;
}
</script>

<template>
  <section
    id="faq"
    class="relative overflow-hidden pb-[clamp(4rem,10vw,8rem)] pt-14 md:pt-20"
  >
    <div class="section-shell relative">
      <AnimatedContent :distance="24">
        <p class="eyebrow text-center">{{ t('faq.eyebrow') }}</p>
      </AnimatedContent>
      <SectionTitle :title="t('faq.title')" />
      <AnimatedContent :delay="0.05" :distance="20" class-name="-mt-7">
        <p class="mx-auto max-w-2xl text-center text-[15px] leading-7 text-muted-foreground sm:text-base md:text-lg">
          {{ t('faq.subtitle') }}
        </p>
      </AnimatedContent>

      <AnimatedContent :delay="0.1" :distance="24" class-name="mt-10 md:mt-14">
        <!-- 手机：手风琴，点开即见答案 -->
        <div class="border-t border-slate-200/80 md:hidden">
          <div
            v-for="(item, index) in items"
            :key="`m-${item.id}`"
            class="border-b border-slate-200/80"
          >
            <button
              type="button"
              class="flex w-full items-start gap-3 py-3.5 text-left"
              :aria-expanded="activeIndex === index"
              @click="selectFaq(index)"
            >
              <span
                class="mt-0.5 w-6 shrink-0 text-[12px] font-medium tabular-nums tracking-wide"
                :class="activeIndex === index ? 'text-primary' : 'text-slate-400'"
              >
                {{ String(item.id).padStart(2, '0') }}
              </span>
              <span
                class="min-w-0 flex-1 text-sm font-medium leading-snug tracking-tight"
                :class="activeIndex === index ? 'text-foreground' : 'text-slate-600'"
              >
                {{ item.q }}
              </span>
              <span
                class="mt-1 shrink-0 text-slate-400 transition-transform duration-200"
                :class="activeIndex === index ? 'rotate-45 text-primary' : ''"
                aria-hidden="true"
              >
                <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 5v14M5 12h14" stroke-linecap="round" />
                </svg>
              </span>
            </button>

            <div
              v-show="activeIndex === index"
              class="pb-4 pl-9 pr-1"
            >
              <p class="text-[13px] leading-6 text-muted-foreground">
                {{ item.a }}
              </p>
            </div>
          </div>
        </div>

        <!-- 平板+：左列表 / 右答案 -->
        <div class="hidden items-start gap-10 md:grid lg:grid-cols-12 lg:gap-14">
          <nav class="lg:col-span-5" :aria-label="t('faq.title')">
            <ul class="max-h-[28rem] overflow-y-auto border-t border-slate-200/80 pr-1 sm:max-h-[32rem]">
              <li
                v-for="(item, index) in items"
                :key="item.id"
                class="border-b border-slate-200/80"
              >
                <button
                  type="button"
                  class="group flex w-full items-baseline gap-3 py-3.5 text-left transition-colors"
                  :aria-current="activeIndex === index ? 'true' : undefined"
                  @click="selectFaq(index)"
                  @mouseenter="onFaqHover(index)"
                >
                  <span
                    class="w-6 shrink-0 text-[12px] font-medium tabular-nums tracking-wide"
                    :class="activeIndex === index ? 'text-primary' : 'text-slate-400'"
                  >
                    {{ String(item.id).padStart(2, '0') }}
                  </span>
                  <span
                    class="min-w-0 text-[15px] font-medium leading-snug tracking-tight transition-colors"
                    :class="activeIndex === index ? 'text-foreground' : 'text-slate-500 group-hover:text-slate-700'"
                  >
                    {{ item.q }}
                  </span>
                </button>
              </li>
            </ul>
          </nav>

          <div class="lg:col-span-7 lg:sticky lg:top-28">
            <p class="text-sm font-medium text-primary">
              {{ String(current.id).padStart(2, '0') }}
            </p>
            <h3 class="mt-3 text-2xl font-bold leading-snug tracking-tight text-foreground">
              {{ current.q }}
            </h3>
            <p class="mt-5 text-base leading-7 text-muted-foreground">
              {{ current.a }}
            </p>
          </div>
        </div>
      </AnimatedContent>
    </div>
  </section>
</template>
